import { useRef } from "react";
import type { MouseEvent, ReactNode } from "react";
import type { NavGroup, NavPage } from "../features/registry";
import { useNavDrawer } from "../hooks/useNavDrawer";
import { AccountMenu } from "./AccountMenu";
import { LeagueBadge } from "./LeagueBadge";
import { LogoLightbox } from "./LogoLightbox";
import { NavDrawerPanel, NavDrawerToggle } from "./NavDrawer";
import { NavTabs, navigateOnPlainClick } from "./NavTabs";
import { SectionTabs } from "./SectionTabs";
import { YahooAttribution } from "./YahooAttribution";

interface LayoutProps {
  leagueName: string;
  /** The nav's groups (`NAV_GROUPS` in `features/registry.tsx`) and where the visitor is in them. */
  groups: NavGroup[];
  active: { group: NavGroup; page: NavPage } | null;
  onNavigate: (path: string) => void;
  /**
   * Login/logout + edit-mode control, rendered in the nav drawer's Account
   * section. A render prop (rather than a plain ReactNode) so its buttons
   * can close the drawer themselves — e.g. the edit-mode toggle, which
   * should dismiss the drawer immediately rather than leaving it open over
   * the form it just revealed.
   */
  accountSlot?: (closeDrawer: () => void) => ReactNode;
  /**
   * Logged-in user's email, or null when logged out — drives the desktop
   * header's avatar menu (see AccountMenu). Not needed by the drawer, which
   * gets everything it needs from `accountSlot` already.
   */
  accountEmail: string | null;
  /** Site-wide footer lines (e.g. "Data updated …"), shown under the league-history line. */
  footer?: ReactNode;
  children: ReactNode;
}

export function Layout({
  leagueName,
  groups,
  active,
  onNavigate,
  accountSlot,
  accountEmail,
  footer,
  children,
}: LayoutProps) {
  const drawer = useNavDrawer();
  const mainRef = useRef<HTMLElement>(null);

  function skipToMain(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();
    mainRef.current?.focus();
  }
  // Looked up by path rather than position, so reordering the groups
  // can't quietly point the brand link somewhere other than home.
  const homePage = groups.flatMap((group) => group.pages).find((page) => page.path === "/");

  return (
    <div className="layout">
      {/* First tab stop on every page. Focuses <main> directly rather than
          following the #main hash, so the URL doesn't pick up a fragment. */}
      <a className="skip-link" href="#main" onClick={skipToMain}>
        Skip to content
      </a>
      <header className="layout__header">
        <NavDrawerToggle isOpen={drawer.isOpen} onOpen={drawer.open} buttonRef={drawer.toggleRef} />
        {/* Two titles, one per width, each display:none at the other (see
            .layout__title--mobile/.layout__brand in app.css), so only one
            <h1> is ever in the accessibility tree. Both link home. Below
            1024px that's the name alone (the drawer holds the rest of the
            navigation); at >=1024px, the badge + name lockup, like a desktop
            site's logo. */}
        {homePage ? (
          <a
            href={homePage.path}
            className="layout__home-link layout__title--mobile"
            onClick={navigateOnPlainClick(() => onNavigate(homePage.path))}
          >
            <h1 className="layout__title">{leagueName}</h1>
          </a>
        ) : (
          <h1 className="layout__title layout__title--mobile">{leagueName}</h1>
        )}
        {homePage && (
          <a href={homePage.path} className="layout__brand" onClick={navigateOnPlainClick(() => onNavigate(homePage.path))}>
            <LeagueBadge className="layout__brand-badge" size={40} />
            <h1 className="layout__title">{leagueName}</h1>
          </a>
        )}
        <NavTabs groups={groups} activeGroupId={active?.group.id ?? null} onNavigate={onNavigate} />
        {/* Inline account slot for >=1024px (see .layout__header-account in
            app.css). Logged in: an avatar button opening a dropdown with
            the same content the drawer shows, via a no-op closeDrawer since
            there's no drawer to close from here. Logged out: accountSlot's
            own "Log in" button, unwrapped. The drawer keeps its own real
            closeDrawer below either way. */}
        <div className="layout__header-account">
          {accountEmail ? (
            <AccountMenu email={accountEmail}>{(closeMenu) => accountSlot?.(closeMenu)}</AccountMenu>
          ) : (
            accountSlot?.(() => {})
          )}
        </div>
        {/* Below 1024px the nav/account slots above are hidden (drawer holds
            them instead), which leaves the header's right edge empty next to
            the title — show the app's badge there, tappable to view it full
            screen. Hidden again at >=1024px, where NavTabs/the account menu
            already occupy that space and the badge moves into the
            .layout__brand lockup instead (see .layout__logo-button in app.css). */}
        <LogoLightbox leagueName={leagueName} />
      </header>

      <NavDrawerPanel
        isOpen={drawer.isOpen}
        onClose={drawer.close}
        groups={groups}
        activePath={active?.page.path ?? null}
        onNavigate={onNavigate}
        accountSlot={accountSlot?.(drawer.close)}
        panelRef={drawer.panelRef}
      />

      <main id="main" className="layout__main" tabIndex={-1} ref={mainRef}>
        {active && <SectionTabs group={active.group} page={active.page} />}
        {children}
      </main>

      <SiteFooter>{footer}</SiteFooter>
    </div>
  );
}

/**
 * One footer for every page, in two groups: the site's own notes about its
 * data (history scope, freshness) and the Yahoo attribution. Side by side
 * from 600px up, stacked below that, notes first.
 */
function SiteFooter({ children }: { children?: ReactNode }) {
  return (
    <footer className="layout__footer">
      <div className="layout__footer-info">
        {/* 2016 is the fetcher's MIN_SEASON (packages/fetcher/src/discovery.ts) — keep the two in step. */}
        <p className="layout__footer-line">League history covers 2016 onward, including playoff games.</p>
        {children && <p className="layout__footer-line layout__footer-line--meta">{children}</p>}
      </div>
      <YahooAttribution />
    </footer>
  );
}
